import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Pagination, SearchInput, TableSkeleton } from "../../../components/ui";
import { getAdminActivity, getAdminActivityAdmins } from "../../../services/monitoring";
import { EVENT_LABELS, formatDate } from "./shared";

const OUTCOMES = [
  { value: "", label: "All" },
  { value: "failed", label: "Refused or failed" },
  { value: "logins", label: "Logins" },
];

const outcomeOf = (item) => {
  if (item.event === "admin.login") return { color: "blue", label: "Signed in", icon: "ri-login-box-line" };
  if (item.event === "admin.login_failed") return { color: "amber", label: "Refused", icon: "ri-lock-line" };
  if (item.level === "info") return { color: "green", label: "Done", icon: "ri-check-line" };
  if (item.status === 403 || item.status === 401) return { color: "amber", label: "Refused", icon: "ri-forbid-line" };
  return { color: "red", label: "Failed", icon: "ri-close-line" };
};

const daysAgo = (date) => {
  if (!date) return null;
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  return days;
};

/**
 * Activity of the administrators: actions that change something (create,
 * edit, delete, send, run) and back-office logins. The content of the
 * requests is never kept.
 */
const AdminActivity = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [admin, setAdmin] = useState("");
  const [outcome, setOutcome] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    getAdminActivityAdmins().then(setAdmins);
  }, []);

  const load = useCallback(
    () => getAdminActivity({ admin, outcome, q: search, page }).then(setData),
    [admin, outcome, search, page]
  );
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const reset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };
  const active = admins.filter((a) => a.actions30d > 0);
  const items = data?.items || [];

  return (
    <div className="logs-section">
      {active.length > 0 && (
        <div className="audit-admins" aria-label={t("Most active admins (30 days)")}>
          {active.slice(0, 8).map((a) => {
            const days = daysAgo(a.lastActivity);
            return (
              <button
                key={a.id}
                type="button"
                className="ui-card audit-admin"
                aria-pressed={admin === a.id}
                onClick={() => reset(setAdmin)(admin === a.id ? "" : a.id)}
              >
                <span className="audit-avatar" aria-hidden="true">
                  {(a.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="audit-admin-text">
                  <strong>{a.name}</strong>
                  <span>
                    {t("actionsCount", { count: a.actions30d })}
                    {days !== null && ` · ${days === 0 ? t("today") : t("daysAgo", { count: days })}`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <section className="ui-card">
        <div className="ui-toolbar logs-toolbar">
          <div className="ui-chips">
            {OUTCOMES.map((o) => (
              <button
                key={o.value}
                type="button"
                className="ui-chip"
                aria-pressed={outcome === o.value}
                onClick={() => reset(setOutcome)(o.value)}
              >
                {t(o.label)}
              </button>
            ))}
            <select
              className="ui-input logs-select"
              value={admin}
              aria-label={t("Administrator")}
              onChange={(e) => reset(setAdmin)(e.target.value)}
            >
              <option value="">{t("All administrators")}</option>
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="logs-toolbar-end">
            <SearchInput value={search} onChange={reset(setSearch)} placeholder={t("Action, page or IP address")} />
            <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />
          </div>
        </div>

        {!data ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-shield-user-line">{t("No activity recorded")}</EmptyState>
        ) : (
          <ul className="logs-events">
            {items.map((item) => {
              const state = outcomeOf(item);
              const isOpen = open === item.id;
              return (
                <li key={item.id} data-open={isOpen || undefined}>
                  <button
                    type="button"
                    className="logs-event-row audit-row"
                    onClick={() => setOpen(isOpen ? null : item.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="logs-event-date">{formatDate(item.at)}</span>
                    <Badge color={state.color} dot>
                      {t(state.label)}
                    </Badge>
                    <span className="logs-event-category">{item.admin || item.email || "—"}</span>
                    <span className="logs-event-text">
                      <strong>{item.action || t(EVENT_LABELS[item.event] || item.event)}</strong>
                      {item.path && (
                        <span>
                          {item.method} {item.path}
                        </span>
                      )}
                    </span>
                    {item.count > 1 && <span className="logs-count">×{item.count}</span>}
                    <i className="ri-arrow-down-s-line logs-chevron" aria-hidden="true"></i>
                  </button>
                  {isOpen && (
                    <div className="logs-event-details">
                      <dl className="audit-details">
                        <dt>{t("Administrator")}</dt>
                        <dd>{item.admin || item.email || "—"}</dd>
                        {item.target && (
                          <>
                            <dt>{t("Target")}</dt>
                            <dd>
                              <code>{item.target}</code>
                            </dd>
                          </>
                        )}
                        {item.status && (
                          <>
                            <dt>{t("Response")}</dt>
                            <dd>
                              <code>{item.status}</code>
                              {item.durationMs !== null && ` · ${item.durationMs} ms`}
                            </dd>
                          </>
                        )}
                        {item.ip && (
                          <>
                            <dt>{t("IP address")}</dt>
                            <dd>
                              <code>{item.ip}</code>
                            </dd>
                          </>
                        )}
                        {item.device && (
                          <>
                            <dt>{t("Computer")}</dt>
                            <dd>{item.device}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {data && (
          <Pagination
            page={page}
            pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))}
            total={data.total}
            pageSize={data.pageSize}
            onChange={setPage}
          />
        )}
      </section>
      <p className="ui-hint logs-note">
        {t("Every change made by an administrator and every back-office login is kept 400 days. The content of the requests (texts, passwords, files) is never recorded.")}
      </p>
    </div>
  );
};

export default AdminActivity;
